import React, { useEffect, useState, useRef } from 'react';
import { X, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { LogoMark } from './Logo';
type QuickAction = {
  key: string;
  label: string;
};
type Message = {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  actions?: QuickAction[];
  cta?: {
    label: string;
    to: string;
  };
  list?: string[];
};
const MENU_ACTIONS: QuickAction[] = [{
  key: 'reservar',
  label: '📅 Reservar un turno'
}, {
  key: 'servicios',
  label: '✂️ Ver servicios'
}, {
  key: 'barberos',
  label: '👨‍🦱 Conocer a los barberos'
}, {
  key: 'pago',
  label: '💳 ¿Cómo funciona el pago?'
}, {
  key: 'faq',
  label: '❓ Preguntas frecuentes'
}];
const FAQ_ITEMS = ['¿Puedo cancelar un turno?', '¿Hasta cuándo puedo reprogramar?', '¿Qué pasa si llego tarde?', '¿Cómo recibo la confirmación?', '¿Aceptan efectivo en el local?', '¿Cómo contacto a la barbería?'];
const SERVICE_ITEMS = ['Corte clásico', 'Fade', 'Barba', 'Corte + Barba', 'Coloración', 'Otros servicios'];
function buildBotResponse(key: string): Message[] {
  const id = () => `b_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  switch (key) {
    case 'reservar':
      return [{
        id: id(),
        sender: 'bot',
        text: '¡Es muy fácil! Primero elegí el servicio que querés, después seleccioná tu barbero favorito, elegí el día y la hora disponibles y, por último, realizá el pago para confirmar la reserva.',
        cta: {
          label: 'Ir a Reservas',
          to: '/reservar'
        }
      }];
    case 'servicios':
      return [{
        id: id(),
        sender: 'bot',
        text: 'Estos son algunos de nuestros servicios:',
        list: SERVICE_ITEMS,
        cta: {
          label: 'Ver todos los servicios',
          to: '/servicios'
        }
      }];
    case 'barberos':
      return [{
        id: id(),
        sender: 'bot',
        text: 'Trabajamos con barberos especializados. Podés ver su experiencia, puntuación, trabajos y horarios antes de reservar.',
        cta: {
          label: 'Ver barberos',
          to: '/barberos'
        }
      }];
    case 'pago':
      return [{
        id: id(),
        sender: 'bot',
        text: 'Para confirmar tu turno se solicita el pago online mediante Mercado Pago. Una vez aprobado, el horario queda reservado automáticamente.'
      }];
    case 'faq':
      return [{
        id: id(),
        sender: 'bot',
        text: 'Estas son algunas preguntas frecuentes:',
        list: FAQ_ITEMS
      }];
    default:
      return [{
        id: id(),
        sender: 'bot',
        text: 'Puedo ayudarte a reservar un turno, ver servicios, conocer a los barberos o resolver dudas. Elegí una opción 👇',
        actions: MENU_ACTIONS
      }];
  }
}
const WELCOME_MESSAGE: Message = {
  id: 'welcome',
  sender: 'bot',
  text: '👋 ¡Bienvenido a BarberOS! Estoy para ayudarte a sacar el máximo provecho de la aplicación. ¿Qué querés hacer?',
  actions: MENU_ACTIONS
};
export function ChatBot() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const teasered = useRef(false);
  // Show a small, non-blocking welcome teaser 3-5s after load.
  // It sits beside the chat button and never covers the home screen.
  useEffect(() => {
    if (teasered.current) return;
    teasered.current = true;
    const delay = 3000 + Math.random() * 2000;
    const timer = setTimeout(() => setShowTeaser(true), delay);
    return () => clearTimeout(timer);
  }, []);
  const openChat = () => {
    setShowTeaser(false);
    setIsOpen(true);
  };
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isOpen]);
  const handleAction = (action: QuickAction) => {
    const userMsg: Message = {
      id: `u_${Date.now()}`,
      sender: 'user',
      text: action.label
    };
    setMessages(prev => [...prev, userMsg]);
    setTimeout(() => {
      setMessages(prev => [...prev, ...buildBotResponse(action.key)]);
    }, 500);
  };
  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    const text = inputValue.trim();
    if (!text) return;
    setMessages(prev => [...prev, {
      id: `u_${Date.now()}`,
      sender: 'user',
      text
    }]);
    setInputValue('');
    setTimeout(() => {
      setMessages(prev => [...prev, ...buildBotResponse('menu')]);
    }, 500);
  };
  const handleCta = (to: string) => {
    setIsOpen(false);
    navigate(to);
  };
  return <>
      {/* Non-blocking welcome teaser — appears beside the button, never over the page */}
      <AnimatePresence data-id="element-119">
        {showTeaser && !isOpen && <motion.div initial={{
        opacity: 0,
        y: 12,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.95
      }} transition={{
        duration: 0.25
      }} className="fixed bottom-40 right-4 sm:right-6 z-50 w-60 max-w-[75vw]" data-id="element-120">
            <button onClick={openChat} className="block w-full text-left bg-dark-card border border-white/10 rounded-2xl rounded-br-sm p-3.5 shadow-2xl hover:border-gold/40 transition-colors" data-id="element-121">
              <div className="flex items-center gap-2 mb-1.5" data-id="element-122">
                <LogoMark size={18} color="#C8A548" className="flex-shrink-0" data-id="element-123" />
                <span className="text-[11px] font-medium text-light" data-id="element-124">
                  BarberOS Assistant
                </span>
              </div>
              <p className="text-[12px] text-light-muted leading-snug" data-id="element-125">
                👋 ¡Hola! Estoy para ayudarte a reservar tu turno. Tocá para
                empezar.
              </p>
            </button>
            <button onClick={() => setShowTeaser(false)} aria-label="Cerrar mensaje" className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-dark border border-white/10 flex items-center justify-center text-light-muted hover:text-light shadow-lg" data-id="element-126">
              <X size={12} data-id="element-127" />
            </button>
          </motion.div>}
      </AnimatePresence>

      <motion.button initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} onClick={openChat} aria-label="Abrir chat de ayuda" className={`fixed bottom-24 right-4 sm:right-6 w-14 h-14 rounded-2xl bg-dark-card border border-gold/30 text-gold shadow-xl z-50 items-center justify-center hover:border-gold/60 transition-colors ${isOpen ? 'hidden' : 'flex'}`} data-id="element-128">
        <LogoMark size={32} color="#C8A548" data-id="element-129" />
        {showTeaser && <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-dark" data-id="element-130" />}
      </motion.button>

      <AnimatePresence data-id="element-131">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsOpen(false)} className="fixed inset-0 bg-black/50 z-40" data-id="element-132" />
            <motion.div initial={{
          opacity: 0,
          y: 24,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 24,
          scale: 0.98
        }} transition={{
          duration: 0.2
        }} role="dialog" aria-label="Chat de ayuda de BarberOS" className="fixed bottom-0 right-0 left-0 mx-auto w-full max-w-md h-[75vh] max-h-[560px] sm:bottom-6 sm:right-6 sm:left-auto sm:mx-0 sm:w-[360px] sm:h-[520px] bg-dark rounded-t-3xl sm:rounded-3xl shadow-2xl z-50 flex flex-col border border-white/10 overflow-hidden" data-id="element-133">
              {/* Header */}
              <div className="p-3.5 flex items-center gap-3 border-b border-white/5 flex-shrink-0" data-id="element-134">
                <LogoMark size={28} color="#C8A548" className="flex-shrink-0" data-id="element-135" />
                <div className="flex-1 min-w-0" data-id="element-136">
                  <h3 className="font-medium text-sm text-light leading-tight" data-id="element-137">
                    BarberOS Assistant
                  </h3>
                  <p className="text-[10px] text-light-muted flex items-center gap-1" data-id="element-138">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" data-id="element-139" />
                    En línea
                  </p>
                </div>
                <button onClick={() => setIsOpen(false)} aria-label="Cerrar chat" className="p-1.5 text-light-muted hover:text-gold transition-colors" data-id="element-140">
                  <X size={18} data-id="element-141" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3.5 space-y-3 scrollbar-hide" data-id="element-142">
                {messages.map(msg => <div key={msg.id} className="space-y-2" data-id="element-143">
                    <div className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-144">
                      <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[13px] whitespace-pre-line leading-relaxed ${msg.sender === 'user' ? 'bg-gold/20 text-gold rounded-br-sm border border-gold/30' : 'bg-dark-card text-light rounded-bl-sm border border-white/5'}`} data-id="element-145">
                        {msg.text}
                      </div>
                    </div>

                    {/* Inline list (services / faqs) */}
                    {msg.list && <div className="flex justify-start" data-id="element-146">
                        <ul className="max-w-[85%] bg-dark-card border border-white/5 rounded-2xl rounded-bl-sm px-3.5 py-2.5 space-y-1.5" data-id="element-147">
                          {msg.list.map(item => <li key={item} className="text-[12px] text-light-muted flex items-start gap-2" data-id="element-148">
                              <span className="text-gold mt-0.5" data-id="element-149">•</span>
                              <span data-id="element-150">{item}</span>
                            </li>)}
                        </ul>
                      </div>}

                    {/* CTA button */}
                    {msg.cta && <div className="flex justify-start" data-id="element-151">
                        <button onClick={() => handleCta(msg.cta!.to)} className="text-[12px] font-semibold px-4 py-2 rounded-xl bg-gold text-dark hover:bg-gold-hover transition-colors" data-id="element-152">
                          {msg.cta.label}
                        </button>
                      </div>}

                    {/* Quick action buttons */}
                    {msg.actions && <div className="flex flex-col items-start gap-2 pt-1" data-id="element-153">
                        {msg.actions.map(a => <button key={a.key} onClick={() => handleAction(a)} className="text-[12.5px] text-left px-3.5 py-2 rounded-xl bg-dark-card text-light border border-white/10 hover:border-gold/40 hover:text-gold transition-colors w-full" data-id="element-154">
                            {a.label}
                          </button>)}
                      </div>}
                  </div>)}
                <div ref={messagesEndRef} data-id="element-155" />
              </div>

              {/* Input */}
              <form onSubmit={handleSend} className="p-3 border-t border-white/5 flex gap-2 items-center flex-shrink-0" data-id="element-156">
                <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Escribí tu mensaje..." className="flex-1 bg-dark-card border border-white/5 rounded-xl px-3.5 py-2.5 text-[13px] text-light placeholder:text-light-muted focus:outline-none focus:border-gold/40" data-id="element-157" />
                <button type="submit" disabled={!inputValue.trim()} aria-label="Enviar mensaje" className="p-2.5 rounded-xl bg-gold/15 text-gold disabled:opacity-40 hover:bg-gold/25 transition-colors" data-id="element-158">
                  <Send size={18} data-id="element-159" />
                </button>
              </form>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}